import {
	Box,
	Button,
	CircularProgress,
	FormControlLabel,
	IconButton,
	Paper,
	Switch,
	Typography,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getCreateEmCommentLoading,
	getEmComments,
	getUpdateEmCommentLoading,
	getEmCommentsLoading,
} from 'redux/reducers/emManagement/selectors';
import { useEffect, useMemo, useState } from 'react';
// import { TEmCommentUser } from 'redux/reducers/emManagement/types';
import { ESortDirection } from 'ui/EasTable/types';
import { useParams } from 'react-router-dom';
import { getEmCommentsRequest } from 'redux/reducers/emManagement/reducer';
import { DateTime } from 'luxon';
import { CalendarMonth, Edit, ExpandCircleDown, People } from '@mui/icons-material';
import { HelpButton } from 'ui/HelpButton';
import { getUser } from 'redux/reducers/user/selectors';
// tableStructure kept for future reuse in box conversion but not used now
import { TEmCommentItem, TEmCommentUser } from 'redux/reducers/emManagement/types';
import { TUser } from '../../../../../redux/reducers/user/types';
import { UserAvatar } from '../../../../../ui/UserAvatar';
import { AddAndEditCommentDialog } from './AddAndEditCommentDialog';
import { COLORS } from '../../../../../services/constants/colors';
import { EHelpButtonArticles } from '../../../../../ui/HelpButton/constants';

// removed old form value typing; dialog handles its own state

export const EmComments = () => {
	const dispatch = useDispatch();
	const { emId } = useParams();
	const user = useSelector(getUser);
	const emComments = useSelector(getEmComments);
	const emCommentsLoading = useSelector(getEmCommentsLoading);
	const createEmCommentRequestLoading = useSelector(getCreateEmCommentLoading);
	const updateEmCommentRequestLoading = useSelector(getUpdateEmCommentLoading);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);
	const [isEdit, setIsEdit] = useState(false);
	const [isCommentsExpanded, setIsCommentsExpanded] = useState(false);
	const [isHideNotifications, setIsHideNotifications] = useState(true);
	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};

	useEffect(() => {
		if (emId) {
			dispatch(
				getEmCommentsRequest({
					pageNumber: 1,
					pageSize: PAGE_SIZE,
					orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
					filters: [{ fieldName: 'emId', value: emId, operator: '=' }],
				}),
			);
		}
	}, [dispatch, emId]);

	const displayedComments = useMemo((): TEmCommentItem[] => {
		const data = emComments?.data;
		const all: TEmCommentItem[] = Array.isArray(data) ? data : [];
		return isHideNotifications
			? all.filter((item: TEmCommentItem) => !item?.scheduledActionOn)
			: all;
	}, [emComments, isHideNotifications]);

	return (
		<Box>
			<Box>
				<Paper>
					<Box sx={{ width: 'auto', minWidth: '400px' }}>
						<Box>
							<Box
								sx={{
									backgroundColor: COLORS.BACKGROUND_SECONDARY,
									p: 0.5,
									borderRadius: '12px 12px 0 0',
								}}
							>
								<Box
									sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
								>
									<Typography variant="body1" color="primary" fontWeight="bold">
										Comments
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										{(emCommentsLoading ||
											createEmCommentRequestLoading ||
											updateEmCommentRequestLoading ||
											updateEmCommentRequestLoading) && <CircularProgress size={20} />}

										<EasTooltip title="Hide Comments with Scheduled Actions" sx={{ p: 0, m: 0 }}>
											<FormControlLabel
												control={
													<Switch
														color="primary"
														checked={isHideNotifications}
														onChange={() => {
															setIsHideNotifications(!isHideNotifications);
														}}
														size="small"
														sx={{
															'& .MuiSwitch-thumb': {
																width: '14px',
																height: '14px',
															},
														}}
													/>
												}
												labelPlacement="start"
												color="primary"
												label={
													<Typography variant="body2" color="primary" fontWeight="bold">
														Hide notifications
													</Typography>
												}
											/>
										</EasTooltip>

										<Button
											variant="contained"
											color="primary"
											size="small"
											onClick={() => {
												setIdToEdit(null);
												setIsEdit(false);
												setIsEditDialogOpen(true);
											}}
										>
											Add Comment
										</Button>
										{!!displayedComments.length && isCommentsExpanded && (
											<EasTooltip title="Show less comments">
												<IconButton
													color="primary"
													sx={{ p: 0, m: 0 }}
													onClick={() => {
														setIsCommentsExpanded(false);
													}}
												>
													<ExpandCircleDown
														sx={{ fontSize: '2rem', p: 0, m: 0, transform: 'rotate(180deg)' }}
													/>
												</IconButton>
											</EasTooltip>
										)}
										{!!displayedComments.length && !isCommentsExpanded && (
											<EasTooltip title="Show more comments">
												<IconButton
													color="primary"
													sx={{ p: 0, m: 0 }}
													onClick={() => {
														setIsCommentsExpanded(true);
													}}
												>
													<ExpandCircleDown sx={{ fontSize: '2rem', p: 0, m: 0 }} />
												</IconButton>
											</EasTooltip>
										)}
										<HelpButton type={EHelpButtonArticles.COMMENTS} />
									</Box>
								</Box>
							</Box>
							{!displayedComments.length && (
								<Typography
									sx={{
										p: 1,
										m: 0,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: '50px',
										color: COLORS.SECONDARY,
										fontSize: '24px',
										fontWeight: 'bold',
									}}
								>
									No comments found...
								</Typography>
							)}
							{!!displayedComments.length && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'start',
										gap: 1,
										flexDirection: 'column',
										p: 1,
										overflowX: 'auto',
										maxHeight: isCommentsExpanded ? 'auto' : '200px',
									}}
								>
									{displayedComments.map((item) => (
										<Box
											key={item.id}
											sx={{
												borderRadius: '12px',
												// border: '1px solid #016193',
												backgroundColor:
													item.createdBy.id === user?.userInfo?.id
														? COLORS.SECONDARY
														: COLORS.BACKGROUND_SECONDARY,
												alignSelf: item.createdBy.id === user?.userInfo?.id ? 'end' : 'start',
												m: 0,
												p: 0.5,
											}}
										>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													gap: 1,
													justifyContent: 'space-between',
													m: 0,
													p: 0,
												}}
											>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, p: 0 }}>
													<UserAvatar
														user={item.createdBy ?? ({} as TUser)}
														size="xxs"
														onClick={() => {}}
													/>
													<Typography
														variant="body2"
														color="primary"
														fontWeight="bold"
														fontSize="0.8rem"
													>
														{item.createdAt
															? DateTime.fromISO(item.createdAt).toFormat('yyyy-MM-dd HH.mm')
															: 'Unknown date'}
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, p: 0 }}>
													{item.scheduledActionOn && (
														<EasTooltip
															title={`scheduled action on: ${item.scheduledActionOn.slice(0, 10)}`}
														>
															<CalendarMonth color="primary" fontSize="small" />
														</EasTooltip>
													)}
													{(item.operatorsToNotify ?? ([] as TEmCommentUser[])).length > 0 && (
														<EasTooltip
															title={(item.operatorsToNotify ?? ([] as TEmCommentUser[]))
																.map((operator) => operator.fullName)
																.join(', ')}
														>
															<People color="primary" fontSize="small" />
														</EasTooltip>
													)}
													{item.createdBy.id === user?.userInfo?.id && (
														<EasTooltip title="Edit">
															<IconButton
																color="primary"
																onClick={() => {
																	setIdToEdit(item.id);
																	setIsEdit(true);
																	setIsEditDialogOpen(true);
																}}
																sx={{
																	p: 0,
																	m: 0,
																}}
															>
																<Edit color="primary" fontSize="small" />
															</IconButton>
														</EasTooltip>
													)}
												</Box>
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: 0.5,
													p: 0.5,
												}}
											>
												{(item.operatorsToNotify ?? ([] as TEmCommentUser[])).length > 0 && (
													<Box
														sx={{
															display: 'flex',
															gap: 1,
															flexWrap: 'wrap',
															m: 0,
															p: 0,
														}}
													>
														{(item.operatorsToNotify ?? ([] as TEmCommentUser[])).map(
															(operator) => (
																<Typography
																	variant="body2"
																	fontSize="0.8rem"
																	color={
																		operator.id === user?.userInfo?.id
																			? 'primary'
																			: 'text.secondary'
																	}
																	key={operator.id}
																	sx={{ m: 0, p: 0 }}
																>
																	@{operator.fullName}
																</Typography>
															),
														)}
													</Box>
												)}

												<Typography
													variant="body1"
													color="text.secondary"
													sx={{ fontSize: '0.9rem', m: 0, p: 0 }}
												>
													{item.comment}
												</Typography>
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				</Paper>
			</Box>
			<AddAndEditCommentDialog
				open={isEditDialogOpen}
				setClose={handleEditClose}
				type={isEdit ? 'edit' : 'add'}
				idToEdit={idToEdit}
				initialComment={
					idToEdit ? (emComments?.data?.find((item) => item.id === idToEdit)?.comment ?? '') : ''
				}
				initialScheduledActionOn={
					idToEdit
						? (emComments?.data?.find((item) => item.id === idToEdit)?.scheduledActionOn ?? null)
						: null
				}
				initialOperatorsToNotify={
					idToEdit
						? (emComments?.data?.find((item) => item.id === idToEdit)?.operatorsToNotify ?? [])
						: []
				}
			/>
		</Box>
	);
};
